import type { AxiosError } from "axios";
import moment from "moment";

import type { NavigationService } from "../../../mobile/core/navigation/navigation-service";
import { Routes } from "../../../mobile/core/navigation/routes";
import { logger } from "../../core/logging/logger";
import type { HateoasLink } from "../../utils/hateoas-link";
import { Observable } from "../../utils/observable";
import { ObservableArray } from "../../utils/observable-array";
import type { Keyboard, KeyboardRequest } from "../pincode/keyboard";
import type { PincodeSubmission } from "../pincode/pincode";
import type { PartyAuthenticationService } from "./party-authentication-service";
import type { PartyAuthenticationSession } from "./party-authentication-session";
import { PartyAuthenticationSessionState } from "./party-authentication-session-state";

export enum PartyAuthenticationManagerState {
  INITIATED = "INITIATED",
  SELECT_SESSION = "SELECT_SESSION",
  CAN_START_SESSION = "CAN_START_SESSION",
  START_SESSION = "START_SESSION",
  VERIFY_OTP = "VERIFY_OTP",
  VERIFY_PINCODE = "VERIFY_PINCODE",
  ERROR = "ERROR",
}

const SESSION_FILTER_THRESHOLD = {
  hours: 12,
};

export class PartyAuthenticationManager {
  public loading = new Observable<boolean>(false);

  public error = new Observable<Error | null>(null);

  public state = new Observable<PartyAuthenticationManagerState>(PartyAuthenticationManagerState.INITIATED);

  public sessions = new ObservableArray<PartyAuthenticationSessionState>([]);
  public currentSession = new Observable<PartyAuthenticationSessionState | null>(null);

  constructor(
    private readonly service: PartyAuthenticationService,
    private readonly navigationService: NavigationService,
  ) {}

  public async initialize(): Promise<void> {
    await this.autoStart();
  }

  public async refreshSessionList(): Promise<void> {
    return await this.loadingWrapper(async () => await this.service.list()).then(
      async (data: Array<PartyAuthenticationSession>) => {
        /**
         * We filter out sessions that are expired in the last 12 hours or in the future.
         */
        const items = data
          .filter((session) =>
            moment(session.expiredAt).isAfter(moment(new Date()).subtract(SESSION_FILTER_THRESHOLD.hours, "hours")),
          )
          .map((session) => new PartyAuthenticationSessionState(session));
        this.sessions.set(items);
      },
      async (error: AxiosError) => {
        this.error.set(error);
        this.state.set(PartyAuthenticationManagerState.ERROR);
        logger.debug("PartyAuthenticationManager", "refreshSessionList", error);
      },
    );
  }

  public async autoStart() {
    await this.refreshSessionList();

    const pendingSessions = this.sessions.get().filter((session) => session.canStart());
    if (pendingSessions.length > 0) {
      this.state.set(PartyAuthenticationManagerState.SELECT_SESSION);
      try {
        if (pendingSessions.length === 1) {
          await this.selectSession(pendingSessions[0].token);
          const session = this.currentSession.get();
          if (session) {
            this.navigationService.navigate(Routes.PartyAuthentication, {
              token: session.token,
            });
          }
        } else {
          this.navigationService.navigate(Routes.PartyAuthenticationList);
        }
      } catch (error) {
        logger.debug(
          "PartyAuthenticationManager",
          "Error while navigating, probably because logging is required",
          error,
        );
      }
    }
  }

  public async selectSession(token: string) {
    await this.loadingWrapper(async () => await this.service.getSession(token))
      .then((session) => {
        this.error.set(null);
        this.currentSession.set(new PartyAuthenticationSessionState(session));
        this.state.set(PartyAuthenticationManagerState.CAN_START_SESSION);
      })
      .catch((error: AxiosError) => {
        this.error.set(error);
        this.state.set(PartyAuthenticationManagerState.ERROR);
      });
  }

  public async getSession(token: string) {
    logger.debug("PartyAuthenticationManager", "getSession", token);
    let session;
    try {
      session = await this.service.getSession(token);
      this.currentSession.set(new PartyAuthenticationSessionState(session));
    } catch (e) {
      logger.debug("PartyAuthenticationManager", "getSession failed", e);
      this.error.set(e);
    }
    return session;
  }

  public async cancelSession(cancelLink?: HateoasLink | null) {
    let link;
    if (cancelLink) {
      link = cancelLink;
    } else {
      link = this.currentSession.get()!.getCancelLink()!;
    }
    await this.loadingWrapper(() => this.service.authenticationSessionCancel(link));
  }

  public async refuseSession() {
    await this.loadingWrapper(() => this.service.authenticationSessionRefuse(this.currentSession.get()!.token));
  }

  public async markSuspicious() {
    await this.loadingWrapper(() => this.service.authenticationSessionMarkSuspicious(this.currentSession.get()!.token));
  }

  public async startSession() {
    this.state.set(PartyAuthenticationManagerState.START_SESSION);

    if (this.currentSession.get()?.canSendSmsOtp()) {
      this.state.set(PartyAuthenticationManagerState.VERIFY_OTP);
      await this.sendSmsOtp();
    } else if (this.currentSession.get()?.canVerifyPincode()) {
      this.state.set(PartyAuthenticationManagerState.VERIFY_PINCODE);
    }
  }

  public async sendSmsOtp(smsOtpLink?: HateoasLink | null) {
    logger.debug("PartyAuthenticationManager", "sendSmsOtp", this.state.get());
    let link;
    if (smsOtpLink) {
      link = smsOtpLink;
    } else {
      link = this.currentSession.get()!.getSmsOtpLink()!;
    }
    return await this.statusUpdate(async () => await this.service.authenticationSessionSendSMSOTP(link));
  }

  public async verifySmsOtp(code: string, verifySmsOtpLink?: HateoasLink | null) {
    let link;
    if (verifySmsOtpLink) {
      link = verifySmsOtpLink;
    } else {
      link = this.currentSession.get()!.getVerifySmsOtpLink()!;
    }
    return await this.statusUpdate(async () => await this.service.authenticationSessionVerifySMSOTP(link, code));
  }

  public async generatePincodeKeyboard(keyboardRequest: KeyboardRequest, token?: string): Promise<Keyboard> {
    logger.debug("PartyAuthenticationManager", "generatePincodeKeyboard", keyboardRequest);
    let sessionToken;
    if (token) {
      sessionToken = token;
    } else {
      sessionToken = this.currentSession.get()!.token;
    }
    return await this.service.generateVirtualKeyboard(sessionToken, keyboardRequest);
  }

  public async verifyPinCode(pincode: PincodeSubmission, verifyPincodeLink?: HateoasLink | null) {
    let link;
    if (verifyPincodeLink) {
      link = verifyPincodeLink;
    } else {
      link = this.currentSession.get()!.getVerifyPincodeLink()!;
    }
    return await this.statusUpdate(async () => await this.service.authenticationSessionVerifyPincode(link, pincode));
  }

  public async verifyTrustedDevice(secret: string, verifyTrustedDeviceLink?: HateoasLink | null) {
    let link;
    if (verifyTrustedDeviceLink) {
      link = verifyTrustedDeviceLink;
    } else {
      link = this.currentSession.get()!.getVerifyTrustedDeviceLink()!;
    }
    return await this.statusUpdate(
      async () => await this.service.authenticationSessionVerifyTrustedDevice(link, secret),
    );
  }

  private async loadingWrapper(cb: CallableFunction) {
    let result;
    try {
      this.loading.set(true);
      this.error.set(null);
      result = await cb();
    } catch (e) {
      this.error.set(e);
      this.state.set(PartyAuthenticationManagerState.ERROR);
      logger.debug("PartyAuthenticationManager", "loadingWrapper failed", e);
      throw e;
    } finally {
      this.loading.set(false);
    }
    return result;
  }

  private async statusUpdate(cb: CallableFunction) {
    let result;
    try {
      result = await this.loadingWrapper(cb);
    } catch (e) {
      result = e;
      logger.debug("PartyAuthenticationManager", "statusUpdate failed", e);
      throw e;
    } finally {
      if (result.links) {
        const sessionState = this.currentSession.get();
        result = sessionState?.update ? sessionState.update(result) : result;
        logger.debug("PartyAuthenticationManager", "Updated session", result);
        this.currentSession.set(result);
      }
    }
    return result;
  }
}
